// @ts-nocheck
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {z} from "zod";
import {BaseButton, BaseCheckbox, BaseDatePicker, BaseInput, BaseSelect, toast} from "@/components";
import {getCookie} from "@/providers/cookieStorage";
import {getMetaPixelParams} from "@/util/urlParams";
import {buildEmailSchema, yearsBetween} from "@/util/validation.util";
import eyeActive from "../../../../assets/images/icon-eye-active.svg";
import eyeInactive from "../../../../assets/images/icon-eye-inactive.svg";
import useUserRegistration from "../../../../hooks/useUserRegistration";
import {useAuthStore} from "../../../../store/useAuth.store";
import {useLogo} from "../../../../util/logoUtils";
import AccountValidation from "../AccountValidation";

const wrapperCls =
  "relative flex items-center justify-center w-full max-w-[920px] box-border max-md:max-w-[440px]";
const ambientGlowCls =
  "pointer-events-none absolute -inset-8 -z-0 [background:radial-gradient(circle_at_20%_15%,rgba(250,84,0,0.18),transparent_55%),radial-gradient(circle_at_80%_85%,rgba(215,50,168,0.22),transparent_55%)]";
const cardCls =
  "relative z-[1] w-full rounded-[28px] border border-white/8 bg-[rgba(20,20,28,0.78)] backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)_inset] overflow-hidden flex flex-row items-stretch max-md:flex-col";
const imageSlotDesktopCls =
  "relative shrink-0 w-[420px] [background:linear-gradient(160deg,rgba(250,84,0,0.10),rgba(215,50,168,0.14))] border-r border-white/6 max-md:hidden";
const imageSlotMobileCls =
  "relative w-full h-[140px] [background:linear-gradient(160deg,rgba(250,84,0,0.10),rgba(215,50,168,0.14))] border-b border-white/6 hidden max-md:block";
const formContainerCls =
  "flex-1 px-8 py-9 max-[480px]:px-6 max-[480px]:py-7 flex flex-col items-center";
const formInnerCls = "w-full max-w-[400px] flex flex-col items-center";
const logoLoginCls =
  "block mx-auto mb-4 max-w-[var(--size-45)] w-[110px] h-auto drop-shadow-[0_8px_24px_rgba(250,84,0,0.35)]";
const titleCls =
  "text-center mb-1 text-[var(--font-h2)] font-bold text-[var(--color-text)] tracking-tight";
const subtitleCls =
  "text-center mb-6 text-[var(--font-body)] text-[var(--color-text-secondary)]";
const fieldsCls = "w-full flex flex-col gap-4";
const inputCls =
  "!h-12 !rounded-full !border !border-white/10 !bg-white/[0.03] !text-[var(--color-text)] !px-5 !text-[var(--font-body-lg)] placeholder:!text-[var(--color-text-tertiary)] !transition-all !duration-200 hover:!border-white/20 focus:!border-[rgba(215,50,168,0.7)] focus:!bg-white/[0.05] focus:!shadow-[0_0_0_4px_rgba(215,50,168,0.12)] focus-visible:!outline-none";
const dateInputCls = `${inputCls} !appearance-none [color-scheme:dark]`;
const submitBtnCls =
  "!w-full !h-12 !rounded-full !font-bold !text-[var(--font-body-lg)] !bg-[linear-gradient(90deg,rgb(250,84,0),rgb(215,50,168))] !text-white !border-0 !shadow-[0_10px_30px_-8px_rgba(215,50,168,0.55)] !transition-all !duration-200 hover:!-translate-y-0.5 hover:!shadow-[0_14px_34px_-8px_rgba(215,50,168,0.7)] active:!translate-y-0 active:!scale-[0.98] disabled:!opacity-60 disabled:!cursor-not-allowed disabled:hover:!translate-y-0 disabled:hover:!shadow-[0_10px_30px_-8px_rgba(215,50,168,0.55)]";
const footerLinksCls =
  "flex flex-wrap justify-center items-center gap-x-2 gap-y-1 mt-5 text-[var(--font-body)]";
const noAccountTextCls = "text-[var(--color-text-secondary)]";
const registerLinkCls =
  "cursor-pointer bg-transparent border-0 p-0 ml-1 font-[var(--font-family)] font-bold text-[var(--color-primary)] transition-colors duration-200 hover:opacity-90 hover:underline underline-offset-4";
const checkboxTextCls = "text-[var(--color-text-secondary)] text-[var(--font-body)] m-0";
const closeBtnCls =
  "absolute top-4 right-4 z-[2] grid place-items-center w-9 h-9 rounded-full bg-white/5 border border-white/10 text-[var(--color-text)] text-base cursor-pointer leading-none transition-all duration-200 hover:bg-white/10 hover:scale-105 active:scale-95 focus-visible:outline-2 focus-visible:outline-[rgba(215,50,168,0.6)]";

const buildSchema = t =>
  z
    .object({
      email: buildEmailSchema(t),
      birthDate: z
        .string()
        .min(1, t("registrationSelectDate"))
        .refine(value => yearsBetween(value, new Date().toISOString()) >= 18, t("registrationBirthdate_message")),
      password: z.string().min(1).min(8, t("registrationPasswordMinLength")),
      repeat_password: z.string().min(1, t("registrationRepeatPassword_required")),
      referral_code: z.string().max(50).optional(),
      preferred_bonus: z.string().optional(),
      terms: z.literal(true, {errorMap: () => ({message: t("registrationAcceptTerms")})}),
    })
    .refine(data => data.password === data.repeat_password, {
      message: t("registrationPasswordNotMatch"),
      path: ["repeat_password"],
    });

const PasswordToggle = ({visible, onToggle}) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label="toggle password"
    style={{background: "transparent", border: "none", padding: 0, cursor: "pointer"}}
  >
    <img
      src={visible ? eyeActive : eyeInactive}
      alt="toggle password"
      style={{width: "var(--size-6)", height: "var(--size-6)"}}
    />
  </button>
);

const RegistrationModalForm = ({onClose}) => {
  const {t} = useTranslation();
  const logo = useLogo();

  const {mutate: postUserRegistration, isPending, error} = useUserRegistration();
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);

  const [values, setValues] = useState({
    email: "",
    birthDate: "",
    password: "",
    repeat_password: "",
    referral_code: "",
    preferred_bonus: "slot",
    terms: false,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [registerButtonClicked, setRegisterButtonClicked] = useState(false);

  useEffect(() => {
    if (!error) return;
    const errorMessage = error?.message || error?.toString();
    if (errorMessage === "user_already_exists") {
      setErrors(prev => ({
        ...prev,
        email: t("registrationEmailAlreadyExists", "Este email ya está registrado"),
      }));
    } else {
      toast.error(`Error en el registro — ${errorMessage || "Ha ocurrido un error inesperado"}`);
    }
  }, [error, t]);

  const setField = (field, value) => {
    setValues(v => ({...v, [field]: value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };

  const handleInput = field => e => setField(field, e.target.value);

  const handleSubmit = e => {
    e.preventDefault();
    setRegisterButtonClicked(true);
    const result = buildSchema(t).safeParse(values);
    if (!result.success) {
      const nextErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    const referred = getCookie("referred");
    const {fbp, fbc} = getMetaPixelParams();
    const sendData = {
      birth_date: result.data.birthDate,
      email: result.data.email,
      password: result.data.password,
      referral_code: referred ?? result.data.referral_code ?? "",
      preferred_bonus: result.data.preferred_bonus || "slot",
    };
    if (fbp) sendData.fbp = fbp;
    if (fbc) sendData.fbc = fbc;
    postUserRegistration(sendData);
  };

  return (
    <div className={wrapperCls}>
      <div
        aria-hidden="true"
        className={ambientGlowCls}
      />
      <div className={cardCls}>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={closeBtnCls}
            aria-label={t("close", "Cerrar")}
          >
            ✕
          </button>
        )}

        <div
          aria-hidden="true"
          className={imageSlotDesktopCls}
        />
        <div
          aria-hidden="true"
          className={imageSlotMobileCls}
        />

        <form
          className={formContainerCls}
          onSubmit={handleSubmit}
          autoComplete="off"
          noValidate
        >
          <div className={formInnerCls}>
            <img
              src={logo}
              alt="logo"
              className={logoLoginCls}
            />
            <h2 className={titleCls}>{t("baseModalTitleRegistration")}</h2>
            <p className={subtitleCls}>{t("registrationSubtitle", "Crea tu cuenta y empieza a ganar")}</p>

            <div className={fieldsCls}>
              <BaseInput
                type="email"
                autoComplete="email"
                placeholder={t("registrationEmail")}
                value={values.email}
                onChange={handleInput("email")}
                error={errors.email}
                className={inputCls}
              />

              <BaseDatePicker
                type="date"
                placeholder={t("registrationSelectDate")}
                value={values.birthDate}
                onChange={value => setField("birthDate", value ?? "")}
                className={dateInputCls}
              />
              {errors.birthDate && (
                <p
                  className="-mt-2 text-[var(--font-body)] text-[var(--color-danger)] px-2"
                  role="alert"
                >
                  {errors.birthDate}
                </p>
              )}

              <BaseInput
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("registrationPassword")}
                value={values.password}
                onChange={handleInput("password")}
                error={errors.password}
                className={inputCls}
                rightIcon={
                  <PasswordToggle
                    visible={showPassword}
                    onToggle={() => setShowPassword(v => !v)}
                  />
                }
              />

              <BaseInput
                type={showRepeatPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("registrationRepeatPassword")}
                value={values.repeat_password}
                onChange={handleInput("repeat_password")}
                error={errors.repeat_password}
                className={inputCls}
                rightIcon={
                  <PasswordToggle
                    visible={showRepeatPassword}
                    onToggle={() => setShowRepeatPassword(v => !v)}
                  />
                }
              />

              <BaseInput
                placeholder={t("registrationReferralCode")}
                value={values.referral_code}
                onChange={handleInput("referral_code")}
                error={errors.referral_code}
                maxLength={50}
                className={inputCls}
              />

              <BaseSelect
                placeholder={t("registrationBonusTitle")}
                value={values.preferred_bonus}
                onChange={e => setField("preferred_bonus", e.target.value)}
                className={inputCls}
                options={[
                  {value: "slot", label: t("registrationFormBonusSlot")},
                  {value: "casino_live", label: t("registrationFormBonusCasino")},
                  {value: "parley", label: t("registrationFormBonusParley")},
                ]}
              />

              <div className="w-full mt-1">
                <BaseCheckbox
                  checked={values.terms}
                  onChange={checked => setField("terms", checked)}
                >
                  <p className={checkboxTextCls}>
                    Acepto los{" "}
                    <a
                      href="/terminos-y-condiciones"
                      target="_blank"
                      rel="noreferrer"
                      style={{color: "var(--color-primary)"}}
                    >
                      términos y condiciones
                    </a>
                    . Y ser mayor de 18 años de edad.
                  </p>
                </BaseCheckbox>
                {errors.terms && (
                  <p
                    className="mt-2 text-[var(--font-body)] text-[var(--color-danger)]"
                    role="alert"
                  >
                    {errors.terms}
                  </p>
                )}
              </div>

              <BaseButton
                type="submit"
                variant="primary"
                disabled={!values.terms}
                loading={isPending}
                className={submitBtnCls}
              >
                {t("registrationAcceptTermsRegister")}
              </BaseButton>
            </div>

            <div className={footerLinksCls}>
              <span className={noAccountTextCls}>¿Ya tienes una cuenta?</span>
              <button
                type="button"
                onClick={() => handleAuthForm("login")}
                className={registerLinkCls}
              >
                Iniciar sesión
              </button>
            </div>

            {registerButtonClicked && error && (
              <AccountValidation
                error={error}
                onClose={() => setRegisterButtonClicked(false)}
              />
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationModalForm;
